import {useHistory, useRouteMatch} from "react-router";
import React, {useEffect, useRef, useState} from "react";
import {useArticulosService, useOrdenesCompraService} from "services/hooks";
import {useSnackbar} from "notistack";
import {Button, Divider} from "@material-ui/core/index";
import {CardArticulo} from "components/picking/IngresoMercaderia/card.articulo";
import {CardHeader} from "components/picking/components/card.header";
import {useConfirm} from 'material-ui-confirm';
import Box from "@material-ui/core/Box";
import {ScanResultDialog} from "components/picking/components/scan.result.dialog";
import {ScanSuccessPopover} from "components/picking/IngresoMercaderia/scan.success.popover";
import {ClickAwayListener} from "@material-ui/core";
import {ScanReceptor} from "components/picking/components/scan.receptor";
import {ManualScan} from "components/picking/components/manual.scan";
import {ResumenView} from "components/picking/IngresoMercaderia/resumen.view";

export function ScanArticulosView(props) {
    const [articulos, _setArticulos] = useState([]);
    const [scanResult, setScanResult] = useState({errorDialogOpen: false, successPopoverOpen: false, found: false, articulo: undefined});
    const [finished, setFinished] = useState(false);
    const scanActive = useRef(true);

    const history = useHistory();
    const match = useRouteMatch();
    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();
    const ArticulosService = useArticulosService();
    const OrdenesCompraService = useOrdenesCompraService();

    const backUrl = '/picking/ingresoMercaderia';
    const idOrdenCompra = match.params.idOrdenCompra;

    const articulosRef = useRef(articulos);

    // Agrupo los renglones de la OC por artículo (un mismo artículo puede venir en varios renglones).
    const setArticulos = renglones => {
        const _articulos = [];
        renglones.forEach(renglon => {
            const existing = _articulos.find(a => a.id === renglon.id);
            if (!existing)
                _articulos.push({
                    id: renglon.id,
                    detalle: renglon.detalle,
                    cantidad: parseFloat(renglon.cantidad) || 0,
                    enOrden: true,
                    scanned: 0
                });
            else
                existing.cantidad += parseFloat(renglon.cantidad) || 0;
        });

        articulosRef.current = _articulos;
        _setArticulos(_articulos);
    }

    useEffect(() => {
        OrdenesCompraService.getArticulosById(
            idOrdenCompra,
            response => setArticulos(response.data.result),
            error => enqueueSnackbar(error, {variant: 'error'})
        );
    }, []);

    const findScannedArticulo = (scannedCode) => {
        return new Promise((resolve) => {
            ArticulosService.getByBarcode(
                scannedCode,
                success => resolve(success.data.count > 0 ? success.data.result : undefined),
                error => enqueueSnackbar(error, {variant: 'error'})
            )
        })
    }

    const onScanCode = async (scannedCode) => {
        const scannedArticulo = await findScannedArticulo(scannedCode);

        // El código escaneado no corresponde a ningún artículo del sistema.
        if (!scannedArticulo) {
            setScanResult({
                errorDialogOpen: true,
                successPopoverOpen: false,
                found: false,
                scannedCode: scannedCode,
                articulo: undefined
            });
            return;
        }

        let articulo = articulosRef.current.find(a => a.id === scannedArticulo.id);

        // Si el artículo no estaba en la orden de compra lo agrego (ingreso puede incluir extras).
        if (!articulo) {
            articulo = {
                id: scannedArticulo.id,
                detalle: scannedArticulo.detalle,
                cantidad: 0,
                enOrden: false,
                scanned: 0
            };
            articulosRef.current = [...articulosRef.current, articulo];
        }

        // Sin tope: se permiten cantidades distintas a las de la OC.
        articulo.scanned = (parseInt(articulo.scanned) || 0) + 1;
        _setArticulos([...articulosRef.current]);

        setScanResult({
            errorDialogOpen: false,
            successPopoverOpen: true,
            found: true,
            scannedCode: scannedCode,
            articulo: articulo
        });
    };

    const navBack = () => history.push(backUrl);

    const onCancel = () => {
        confirm({
            title: '¿Seguro de cancelar?',
            description: 'Si elige CANCELAR se perderá lo escaneado de esta orden de compra.',
            confirmationText: 'Si, cancelar',
            cancellationText: 'No, seguir'
        })
            .then(() => navBack())
            .catch(() => {
            });
    }

    const onFinish = () => setFinished(true);

    const onConfirmIngreso = () => {
        // TODO: Persistir el ingreso de mercadería en el backend cuando se defina qué hacer
        // con la información escaneada (actualizar stock / cerrar OC / etc.).
        enqueueSnackbar('Ingreso registrado (pendiente de procesar en el backend).', {variant: 'success'});
        navBack();
    }

    const closeErrorDialog = () => setScanResult(prev => ({...prev, errorDialogOpen: false}));
    const closeSuccessPopover = () => setScanResult(prev => ({...prev, successPopoverOpen: false}));

    if (finished)
        return (
            <ResumenView
                idOrdenCompra={idOrdenCompra}
                articulos={articulos}
                onVolver={() => setFinished(false)}
                onConfirm={onConfirmIngreso}
            />
        );

    return (
        <>
            <ScanReceptor onScanCode={onScanCode} scanActive={scanActive}/>
            <div style={{height: window.innerHeight - 47, overflowY: 'auto'}}>
                <CardHeader
                    backUrl={backUrl}
                    textLeft={<>Orden de Compra: <b>{idOrdenCompra}</b></>}
                    textRight={
                        <>
                            <b>{articulos.length}</b> artículos
                            <ManualScan onScanCode={onScanCode} scanActive={scanActive}/>
                        </>
                    }
                />
                {articulos.map((articulo, index) =>
                    <React.Fragment key={index}>
                        <CardArticulo
                            id={articulo.id}
                            detalle={articulo.detalle}
                            cantidad={articulo.cantidad}
                            scanned={articulo.scanned}
                            enOrden={articulo.enOrden}
                        />
                        {index !== articulos.length - 1 && (
                            <Divider/>
                        )}
                    </React.Fragment>
                )}

                <Box>
                    <Button
                        size={"medium"}
                        variant={"contained"}
                        className={'mt-2 mb-2'}
                        color={"primary"}
                        style={{width: '90%', height: '3.5rem', margin: '0 5%'}}
                        onClick={onFinish}
                    >
                        Terminar
                    </Button>
                    <Button
                        size={"medium"}
                        variant={"contained"}
                        className={'mt-2 mb-2'}
                        color={"error"}
                        style={{width: '90%', height: '3.5rem', margin: '0 5%'}}
                        onClick={onCancel}
                    >
                        Cancelar
                    </Button>
                </Box>
            </div>

            {scanResult.successPopoverOpen && scanResult.articulo && (
                <ClickAwayListener onClickAway={() => closeSuccessPopover()}>
                    <Box>
                        <ScanSuccessPopover
                            open={scanResult.successPopoverOpen}
                            articulo={scanResult.articulo}
                            onClick={() => closeSuccessPopover()}
                            onClose={(e) => {
                                e.stopPropagation();
                                closeSuccessPopover();
                            }}
                        />
                    </Box>
                </ClickAwayListener>
            )}

            <ScanResultDialog
                open={scanResult.errorDialogOpen}
                scanResult={scanResult}
                notFoundText={'El código escaneado no corresponde a ningún artículo.'}
                fullScreen
                onConfirm={() => closeErrorDialog()}
                confirmText={'Cerrar'}
            />
        </>
    )
}
